import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Link2, Unlink, RefreshCw, Save, Shield, Globe } from 'lucide-react';
import { useTikTokFetch, ShopInfo, formatDate } from '../hooks/useTikTokFetch';
import '../styles/pages.css';

export default function Settings() {
  const { get, post, put, getShopId } = useTikTokFetch();
  const shopId = getShopId();
  const queryClient = useQueryClient();

  const { data: shop, isLoading } = useQuery<ShopInfo>({
    queryKey: ['shop', shopId],
    queryFn: () => get(`/api/shop/${shopId}`),
    enabled: !!shopId,
  });

  const [brandId, setBrandId] = useState('');
  const [commissionRate, setCommissionRate] = useState<number>(shop?.commission_rate ?? 10);
  const [autoApprove, setAutoApprove] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(true);

  const saveMutation = useMutation({
    mutationFn: () =>
      put(`/api/shop/${shopId}/settings`, {
        commission_rate: commissionRate / 100,
        auto_approve_creators: autoApprove,
        tracking_enabled: trackingEnabled,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shop'] }),
  });

  const connectMutation = useMutation({
    mutationFn: () => post(`/api/shop/${shopId}/connect`, { brand_id: brandId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop'] });
      setBrandId('');
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => post(`/api/shop/${shopId}/disconnect`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shop'] }),
  });

  const syncMutation = useMutation({
    mutationFn: () => post(`/api/shop/${shopId}/sync`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shop'] }),
  });

  if (isLoading) {
    return <div className="page-container"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="page-container">
      <h1 className="page-title">Settings</h1>

      {/* Brand Connection */}
      <motion.section
        className="card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="card-header">
          <Link2 size={18} />
          <h2>Affilync Connection</h2>
        </div>
        <div className="card-body">
          {shop?.brand_id ? (
            <div className="connection-status">
              <div className="status-row">
                <span className="badge badge--success">Connected</span>
                <span className="text-secondary">Brand ID: {shop.brand_id}</span>
              </div>
              <button
                className="btn btn--danger"
                onClick={() => disconnectMutation.mutate()}
                disabled={disconnectMutation.isPending}
              >
                <Unlink size={14} />
                {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          ) : (
            <div className="connection-form">
              <p className="text-secondary">
                Connect your Affilync brand account to sync conversions and manage affiliates.
              </p>
              <div className="input-group">
                <input
                  type="text"
                  className="input"
                  placeholder="Enter your Affilync Brand ID"
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                />
                <button
                  className="btn btn--primary"
                  onClick={() => connectMutation.mutate()}
                  disabled={!brandId || connectMutation.isPending}
                >
                  {connectMutation.isPending ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.section>

      {/* Shop Settings */}
      <motion.section
        className="card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="card-header">
          <Shield size={18} />
          <h2>Shop Configuration</h2>
        </div>
        <div className="card-body settings-form">
          <div className="form-field">
            <label>Default Commission Rate</label>
            <div className="slider-row">
              <input
                type="range"
                min="1"
                max="50"
                value={commissionRate}
                onChange={(e) => setCommissionRate(Number(e.target.value))}
                className="slider"
              />
              <span className="slider-value">{commissionRate}%</span>
            </div>
          </div>
          <div className="form-field">
            <label>Auto-approve Creators</label>
            <button
              className={`toggle ${autoApprove ? 'toggle--on' : ''}`}
              onClick={() => setAutoApprove(!autoApprove)}
            >
              <span className="toggle-knob" />
            </button>
          </div>
          <div className="form-field">
            <label>Tracking Enabled</label>
            <button
              className={`toggle ${trackingEnabled ? 'toggle--on' : ''}`}
              onClick={() => setTrackingEnabled(!trackingEnabled)}
            >
              <span className="toggle-knob" />
            </button>
          </div>
          <button
            className="btn btn--primary"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            <Save size={14} />
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </motion.section>

      {/* Sync & Info */}
      <motion.section
        className="card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="card-header">
          <Globe size={18} />
          <h2>Shop Information</h2>
        </div>
        <div className="card-body">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Shop Name</span>
              <span className="info-value">{shop?.shop_name || 'Unknown'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Shop ID</span>
              <span className="info-value mono">{shop?.shop_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Region</span>
              <span className="info-value">{shop?.region || 'Not set'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Verified</span>
              <span className={`badge ${shop?.is_verified ? 'badge--success' : 'badge--warning'}`}>
                {shop?.is_verified ? 'Verified' : 'Unverified'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Sync</span>
              <span className="info-value">{formatDate(shop?.last_sync ?? null)}</span>
            </div>
          </div>
          <button
            className="btn btn--secondary"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            <RefreshCw size={14} className={syncMutation.isPending ? 'spin' : ''} />
            {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </motion.section>
    </div>
  );
}
